import * as React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";
import { UsersInputV2 } from "@features/users-input";
import { WarningMessage } from "@features/empty-list-view";
import { useMemo, useState } from "react";
import { SPACE_SCOPE } from "@constants";
import { updateAssetsOwner } from "@modules/assets";
import { useDispatch } from "react-redux";

const DialogChangeOwner = ({ assets, onClose, afterMutationWorks }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [changePending, setChangePending] = useState(false);
    const [selectedOwner, setSelectedOwner] = useState(null);

    const onHandleSubmit = async () => {
        try {
            setChangePending(true);
            if (selectedOwner) {
                let owner = { ...selectedOwner };
                owner.username = owner.email;
                delete owner.email;
                const data = {
                    owner,
                    assetIds: assets.map((asset) => asset.id),
                };
                await dispatch(updateAssetsOwner(data));
                onClose();
                afterMutationWorks("Change Owner");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setChangePending(false);
        }
    };

    const isDisabled = useMemo(() => {
        return assets.length === 0 || assets.some((asset) => asset.owner.id !== selectedOwner?.id) === false;
    }, [assets, selectedOwner]);

    return (
        <Dialog
            variant={"basic"}
            open={true}
            onClose={() => onClose()}
            sx={{ ".MuiPaper-root": { width: 480, maxWidth: 480 } }}
        >
            <DialogTitle>
                <Typography variant={"subtitle1_700"} color={theme.palette.greySecondary.main}>
                    {t(`common::msg::Please select a new owner`, `Please select a new owner`)}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container flexDirection={"column"} gap={3} py={1}>
                    <UsersInputV2
                        scope={SPACE_SCOPE.PROJECT}
                        placeholder={`${t(`common::msg::Please select a new owner`, `Please select a new owner`)}.`}
                        disabledSearchUsers={false}
                        isAvatarIconRequired={true}
                        isEdit={false}
                        onDeleteUser={() => {
                            setSelectedOwner(null);
                        }}
                        onAddUser={(user) => {
                            setSelectedOwner(user);
                        }}
                        noOptionsText={
                            <Box
                                height={200}
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <Typography>
                                    {t("common::msg::No search results found.", "No search results found.")}
                                </Typography>
                            </Box>
                        }
                    />
                    <WarningMessage
                        message={
                            <Typography>
                                {t(
                                    "common::msg::Changing the owner may result in the existing owner losing permissions and some features being restricted.",
                                    "Changing the owner may result in the existing owner losing permissions and some features being restricted.",
                                )}
                            </Typography>
                        }
                    />
                </Grid>
            </DialogContent>
            <DialogActions>
                <>
                    <Button variant={`text`} onClick={() => onClose()} color={`grey`}>
                        {t(`common::label::Cancel`, `Cancel`)}
                    </Button>
                    <Button
                        variant={`contained`}
                        disabled={changePending || isDisabled}
                        loading={changePending}
                        onClick={(e) => {
                            e.stopPropagation();
                            onHandleSubmit();
                        }}
                        color={`primary`}
                    >
                        {t(`common::label::Change`, `Change`)}
                    </Button>
                </>
            </DialogActions>
        </Dialog>
    );
};

export default DialogChangeOwner;
